import type { InvoiceWithProject } from "@/hooks/use-invoices";
import { resolveInvoiceCustomerLabel } from "@/hooks/use-invoices";
import { effectiveInvoiceType } from "./invoice-plan";
import { calcTotalRevenueYtd } from "./kpi-calculations";

/**
 * Status slik /faktura viser den (VIB-2948). «Forfalt» avledes for sendte
 * fakturaer med passert forfallsdato, fordi PO-syncen bare skriver `overdue`
 * for noen av dem. Databasen er uendret.
 */
export type InvoiceDisplayStatus = "draft" | "sent" | "overdue" | "paid" | "credited" | "credit_note" | "other";

/** Type for visning og filter. Kreditnota og PO-fakturaer uten app-type får egne verdier. */
export type InvoiceKind = "startup" | "interim" | "final" | "upsell" | "credit_note" | "poweroffice";

/** Statusfilteret. `outstanding` = sendt + forfalt. */
export type InvoiceStatusFilter =
  | "all"
  | "draft"
  | "outstanding"
  | "sent"
  | "overdue"
  | "paid"
  | "credited"
  | "credit_note";

export type InvoicePeriod = "ytd" | "last_year" | "last_12m" | "all" | "custom";

export type InvoiceSortKey = "invoice_date" | "project" | "customer" | "amount" | "due_date";
export type InvoiceSortDir = "asc" | "desc";

export interface InvoiceListFilter {
  status: InvoiceStatusFilter;
  type: InvoiceKind | "all";
  /** Fritekst mot kundenavn, prosjektnr, prosjektnavn og fakturanr. */
  search: string;
  /** Inklusivt datovindu på `invoice_date` (YYYY-MM-DD). `null` = ingen grense. */
  from: string | null;
  to: string | null;
  /** Fakturaer som er gjort opp av kreditnota (`creditedInvoiceIds`). */
  credited?: ReadonlySet<string>;
}

export const INVOICE_PAGE_SIZE = 50;

export function invoiceDisplayStatus(
  inv: Pick<InvoiceWithProject, "status" | "due_date"> & { id?: string },
  today: string,
  credited?: ReadonlySet<string>,
): InvoiceDisplayStatus {
  if (inv.status === "paid" && inv.id && credited?.has(inv.id)) return "credited";
  switch (inv.status) {
    case "sent":
      return inv.due_date && inv.due_date < today ? "overdue" : "sent";
    case "draft":
    case "overdue":
    case "paid":
    case "credit_note":
      return inv.status;
    default:
      return "other";
  }
}

/** Hele dager fra forfall til i dag, eller `null` når fakturaen ikke er forfalt. */
export function daysOverdue(inv: Pick<InvoiceWithProject, "status" | "due_date">, today: string): number | null {
  if (invoiceDisplayStatus(inv, today) !== "overdue" || !inv.due_date) return null;
  const ms = Date.parse(`${today}T00:00:00Z`) - Date.parse(`${inv.due_date}T00:00:00Z`);
  return Math.max(0, Math.round(ms / 86_400_000));
}

export function invoiceKind(
  inv: Pick<InvoiceWithProject, "status" | "invoice_type" | "milestone_index" | "poweroffice_status">,
): InvoiceKind {
  if (inv.status === "credit_note" || inv.poweroffice_status === "OutgoingCreditNote") return "credit_note";
  return (effectiveInvoiceType(inv) as InvoiceKind | null) ?? "poweroffice";
}

/** Datovindu for periodefilteret. Egendefinert bruker `from`/`to` fra URL-en. */
export function periodRange(period: InvoicePeriod, today: string): { from: string | null; to: string | null } {
  const year = Number(today.slice(0, 4));
  switch (period) {
    case "ytd":
      return { from: `${year}-01-01`, to: null };
    case "last_year":
      return { from: `${year - 1}-01-01`, to: `${year - 1}-12-31` };
    case "last_12m":
      return { from: `${year - 1}${today.slice(4)}`, to: null };
    default:
      return { from: null, to: null };
  }
}

function matchesStatus(s: InvoiceDisplayStatus, f: InvoiceStatusFilter): boolean {
  if (f === "all") return true;
  if (f === "outstanding") return s === "sent" || s === "overdue";
  return s === f;
}

/** Klient-side filtrering av fakturalisten. Rader uten fakturadato faller utenfor et datovindu. */
export function filterInvoices(
  invoices: InvoiceWithProject[],
  f: InvoiceListFilter,
  today: string,
): InvoiceWithProject[] {
  const q = f.search.trim().toLowerCase();
  return invoices.filter((i) => {
    if (!matchesStatus(invoiceDisplayStatus(i, today, f.credited), f.status)) return false;
    if (f.type !== "all" && invoiceKind(i) !== f.type) return false;
    if (f.from || f.to) {
      if (!i.invoice_date) return false;
      if (f.from && i.invoice_date < f.from) return false;
      if (f.to && i.invoice_date > f.to) return false;
    }
    if (!q) return true;
    return [
      resolveInvoiceCustomerLabel(i),
      i.project?.project_code ?? "",
      i.project?.name ?? "",
      i.invoice_no != null ? String(i.invoice_no) : "",
    ].some((v) => v.toLowerCase().includes(q));
  });
}

const collator = new Intl.Collator("nb-NO", { numeric: true, sensitivity: "base" });

function sortValue(inv: InvoiceWithProject, key: InvoiceSortKey): string | number | null {
  switch (key) {
    case "invoice_date":
      return inv.invoice_date ?? null;
    case "due_date":
      return inv.due_date ?? null;
    case "amount":
      return inv.amount ?? null;
    case "project":
      return inv.project?.project_code || inv.project?.name || null;
    case "customer": {
      const label = resolveInvoiceCustomerLabel(inv);
      return label === "-" ? null : label;
    }
  }
}

/** Stabil sortering. Rader uten verdi havner sist uansett retning. Prosjektnr sorteres numerisk. */
export function sortInvoices(
  invoices: InvoiceWithProject[],
  key: InvoiceSortKey,
  dir: InvoiceSortDir,
): InvoiceWithProject[] {
  const sign = dir === "asc" ? 1 : -1;
  return [...invoices].sort((a, b) => {
    const av = sortValue(a, key);
    const bv = sortValue(b, key);
    if (av == null && bv == null) return 0;
    if (av == null) return 1;
    if (bv == null) return -1;
    if (typeof av === "number" && typeof bv === "number") return sign * (av - bv);
    return sign * collator.compare(String(av), String(bv));
  });
}

/** Under én krone regnes som null, så øreavrunding ikke avgjør. */
const SETTLED_TOLERANCE = 1;

/**
 * Betalte fakturaer som er gjort opp av kreditnota, ikke av en innbetaling.
 * PO gir saldo 0 i begge tilfeller, så `status` er `paid` for begge.
 *
 * - PO-avstemming: poster med samme `po_match_id` er avstemt mot hverandre.
 *   Har gruppen minst én kreditnota og summerer fakturaene og kreditnotaene
 *   til null, er det ingen innbetaling i gruppen, og fakturaene er kreditert.
 *   En delvis kreditert faktura som er betalt for resten, regnes som betalt.
 * - Revehiet-kobling: kreditnotaer med `related_invoice_id` krediterer
 *   fakturaen de peker på når summen av dem dekker fakturabeløpet.
 */
export function creditedInvoiceIds(
  invoices: ReadonlyArray<Pick<InvoiceWithProject, "id" | "status" | "amount" | "po_match_id" | "related_invoice_id">>,
): Set<string> {
  const credited = new Set<string>();

  const groups = new Map<number, typeof invoices[number][]>();
  for (const inv of invoices) {
    if (inv.po_match_id == null) continue;
    const g = groups.get(inv.po_match_id) ?? [];
    g.push(inv);
    groups.set(inv.po_match_id, g);
  }
  for (const g of groups.values()) {
    if (!g.some((i) => i.status === "credit_note")) continue;
    const sum = g.reduce((s, i) => s + (i.amount ?? 0), 0);
    if (Math.abs(sum) >= SETTLED_TOLERANCE) continue;
    for (const i of g) if (i.status === "paid") credited.add(i.id);
  }

  const creditByTarget = new Map<string, number>();
  for (const inv of invoices) {
    if (inv.status !== "credit_note" || !inv.related_invoice_id) continue;
    creditByTarget.set(inv.related_invoice_id, (creditByTarget.get(inv.related_invoice_id) ?? 0) + (inv.amount ?? 0));
  }
  for (const inv of invoices) {
    const credit = creditByTarget.get(inv.id);
    if (inv.status === "paid" && credit != null && (inv.amount ?? 0) + credit < SETTLED_TOLERANCE) credited.add(inv.id);
  }

  return credited;
}

export interface InvoiceSummary {
  /** Sendt + forfalt, inkl. mva (det kundene skylder). */
  outstanding: { amount: number; count: number };
  overdue: { amount: number; count: number; oldestDays: number | null };
  /** `org.total_revenue_ytd` fra KPI-fasiten, eks. mva. */
  invoicedYtd: { amount: number; count: number };
  /** Utkast med send-feil, samme regel som prosjektkortet. */
  unsent: number;
}

export function summarizeInvoices(invoices: InvoiceWithProject[], today: string): InvoiceSummary {
  const s: InvoiceSummary = {
    outstanding: { amount: 0, count: 0 },
    overdue: { amount: 0, count: 0, oldestDays: null },
    invoicedYtd: { amount: calcTotalRevenueYtd(invoices, new Date(`${today}T12:00:00Z`)).current, count: 0 },
    unsent: 0,
  };
  const yearStart = `${today.slice(0, 4)}-01-01`;
  for (const inv of invoices) {
    const status = invoiceDisplayStatus(inv, today);
    if (status === "sent" || status === "overdue") {
      s.outstanding.amount += inv.amount ?? 0;
      s.outstanding.count += 1;
    }
    if (status === "overdue") {
      s.overdue.amount += inv.amount ?? 0;
      s.overdue.count += 1;
      const days = daysOverdue(inv, today);
      if (days != null && (s.overdue.oldestDays == null || days > s.overdue.oldestDays)) s.overdue.oldestDays = days;
    }
    if (status === "draft" && inv.last_send_error) s.unsent += 1;
    if (status !== "draft" && status !== "other" && inv.invoice_date && inv.invoice_date >= yearStart && inv.invoice_date <= today) {
      s.invoicedYtd.count += 1;
    }
  }
  return s;
}
